import React, {useEffect, useState} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useArticles} from '../utils/useArticles';

const STORAGE_KEY = 'koenig-knowledge-sources';

function getKnowledgeSourcesFromStorage() {
    const sourcesStr = localStorage.getItem(STORAGE_KEY);
    return sourcesStr ? JSON.parse(sourcesStr) : [];
}

function updateKnowledgeSourcesInStorage(sourcesArr = []) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sourcesArr));
}

export default function BlogsList() {
    const navigate = useNavigate();
    const location = useLocation();
    const {articles, refreshArticles} = useArticles();
    const [knowledgeSources, setKnowledgeSources] = useState([]);
    const [syncingSourceId, setSyncingSourceId] = useState(null);

    // Initialize knowledge sources from localStorage or create default ones
    useEffect(() => {
        const storedSources = getKnowledgeSourcesFromStorage();
        if (storedSources.length === 0) {
            // Create default sources based on existing articles
            const defaultSources = [
                {
                    id: 'bio-page',
                    name: 'Bio Page',
                    type: 'simple',
                    enabled: true,
                    alwaysOn: true,
                    createdAt: new Date().toISOString()
                }
            ];
            updateKnowledgeSourcesInStorage(defaultSources);
            setKnowledgeSources(defaultSources);
        } else {
            setKnowledgeSources(storedSources);
        }
    }, []);

    // Refresh articles when component mounts or location changes
    useEffect(() => {
        refreshArticles();
    }, [location.pathname, refreshArticles]);

    // Also refresh when articles are updated (custom event from save)
    useEffect(() => {
        const handleArticlesUpdated = () => {
            refreshArticles();
        };
        window.addEventListener('articlesUpdated', handleArticlesUpdated);
        return () => window.removeEventListener('articlesUpdated', handleArticlesUpdated);
    }, [refreshArticles]);

    // Get indexed articles for a source
    const getIndexedArticlesForSource = (sourceId) => {
        // For now, we'll use all articles as indexed articles
        // In a real implementation, articles would be tagged with sourceId
        return articles.map((article, index) => ({
            id: article.id,
            title: article.title || 'Untitled Article',
            url: `/?articleId=${article.id}`,
            date: article.updatedAt || article.createdAt,
            index: index + 1
        }));
    };

    // Handle sync for a source
    const handleSync = async (sourceId) => {
        setSyncingSourceId(sourceId);
        // Simulate sync delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const updatedSources = knowledgeSources.map(source => {
            if (source.id === sourceId) {
                return {
                    ...source,
                    lastSynced: new Date().toISOString(),
                    articleCount: getIndexedArticlesForSource(sourceId).length
                };
            }
            return source;
        });
        
        setKnowledgeSources(updatedSources);
        updateKnowledgeSourcesInStorage(updatedSources);
        setSyncingSourceId(null);
    };

    // Toggle source enabled state
    const handleToggleSource = (sourceId) => {
        const updatedSources = knowledgeSources.map(source => {
            if (source.id === sourceId) {
                return {...source, enabled: !source.enabled};
            }
            return source;
        });
        setKnowledgeSources(updatedSources);
        updateKnowledgeSourcesInStorage(updatedSources);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    // Format article date
    const formatArticleDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    const styles = {
        container: {
            minHeight: '100vh',
            backgroundColor: '#FAFAFB',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: '1.6rem',
            lineHeight: '1.65'
        },
        wrapper: {
            maxWidth: '1152px',
            margin: '0 auto',
            padding: '48px 24px'
        },
        header: {
            marginBottom: '32px'
        },
        title: {
            fontSize: '36px',
            fontWeight: 'bold',
            color: '#394047',
            margin: '0 0 8px 0',
            textAlign: 'center'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#394047',
            margin: '0 0 16px 0'
        },
        connectedSourcesList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '32px'
        },
        simpleSource: {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            backgroundColor: '#F4F5F6',
            borderRadius: '8px',
            gap: '12px'
        },
        checkbox: {
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            accentColor: '#10B981'
        },
        simpleSourceText: {
            fontSize: '14px',
            color: '#394047',
            fontWeight: '500',
            flex: 1
        },
        simpleSourceStatus: {
            fontSize: '12px',
            color: '#626D79',
            marginLeft: '8px'
        },
        menuButton: {
            padding: '4px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: '#626D79',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        sourceCard: {
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #EBEEF0',
            padding: '24px',
            marginBottom: '16px'
        },
        sourceHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
        },
        sourceHeaderLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        refreshIcon: {
            width: '20px',
            height: '20px',
            color: '#626D79',
            cursor: 'pointer'
        },
        sourceName: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#394047'
        },
        sourceHeaderRight: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
        },
        syncStatus: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#626D79'
        },
        syncCheckmark: {
            width: '16px',
            height: '16px',
            color: '#10B981'
        },
        resyncButton: {
            padding: '6px 12px',
            backgroundColor: '#F4F5F6',
            color: '#394047',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        articlesList: {
            marginTop: '16px'
        },
        articleItem: {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: '1px solid #F4F5F6',
            gap: '12px'
        },
        articleNumber: {
            fontSize: '14px',
            fontWeight: '500',
            color: '#626D79',
            minWidth: '24px'
        },
        articleTitle: {
            fontSize: '14px',
            color: '#394047',
            flex: 1,
            textDecoration: 'none'
        },
        articleLink: {
            width: '16px',
            height: '16px',
            color: '#626D79',
            flexShrink: 0
        },
        articleDate: {
            fontSize: '13px',
            color: '#AEB7C1',
            marginLeft: 'auto'
        },
        emptyState: {
            textAlign: 'center',
            padding: '80px 20px'
        },
        emptyIcon: {
            width: '64px',
            height: '64px',
            margin: '0 auto 16px',
            color: '#AEB7C1'
        },
        emptyTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#7C8B9A',
            margin: '0 0 8px 0'
        },
        emptyText: {
            fontSize: '16px',
            color: '#AEB7C1',
            margin: '0 0 24px 0'
        }
    };

    // Separate simple and complex sources
    const simpleSources = knowledgeSources.filter(s => s.type === 'simple');
    const complexSources = knowledgeSources.filter(s => s.type === 'complex' || s.type === 'substack');

    // Create a Substack source from articles if we have articles but no Substack source
    useEffect(() => {
        if (articles.length > 0 && !complexSources.find(s => s.type === 'substack')) {
            const substackSource = {
                id: 'substack-the-silly-point',
                name: 'Substack: The Silly Point',
                type: 'substack',
                enabled: true,
                lastSynced: new Date().toISOString(),
                articleCount: articles.length,
                createdAt: new Date().toISOString()
            };
            const updatedSources = [...knowledgeSources, substackSource];
            setKnowledgeSources(updatedSources);
            updateKnowledgeSourcesInStorage(updatedSources);
        }
    }, [articles.length, knowledgeSources, complexSources]);

    return (
        <div style={styles.container}>
            <div style={styles.wrapper}>
                {/* Header */}
                <div style={styles.header}>
                    <h1 style={styles.title}>
                        Knowledge Sources
                    </h1>
                </div>

                {/* Connected Sources Section */}
                <div>
                    <h2 style={styles.sectionTitle}>Connected Sources</h2>
                    
                    <div style={styles.connectedSourcesList}>
                        {/* Simple Sources */}
                        {simpleSources.map((source) => (
                            <div key={source.id} style={styles.simpleSource}>
                                <input
                                    type="checkbox"
                                    checked={source.enabled}
                                    onChange={() => handleToggleSource(source.id)}
                                    style={styles.checkbox}
                                />
                                <span style={styles.simpleSourceText}>
                                    {source.name}
                                    {source.alwaysOn && (
                                        <span style={styles.simpleSourceStatus}> (Always on)</span>
                                    )}
                                </span>
                                <button style={styles.menuButton} title="More options">
                                    <svg style={{width: '20px', height: '20px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                    </svg>
                                </button>
                            </div>
                        ))}

                        {/* Complex Sources (Substack, etc.) */}
                        {complexSources.map((source) => {
                            const indexedArticles = getIndexedArticlesForSource(source.id);
                            const articleCount = indexedArticles.length;
                            const lastSynced = source.lastSynced || source.createdAt;
                            const isSyncing = syncingSourceId === source.id;

                            return (
                                <div key={source.id} style={styles.sourceCard}>
                                    <div style={styles.sourceHeader}>
                                        <div style={styles.sourceHeaderLeft}>
                                            <svg 
                                                style={styles.refreshIcon}
                                                onClick={() => handleSync(source.id)}
                                                fill="none" 
                                                stroke="currentColor" 
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            <span style={styles.sourceName}>{source.name}</span>
                                        </div>
                                        <div style={styles.sourceHeaderRight}>
                                            <div style={styles.syncStatus}>
                                                <svg style={styles.syncCheckmark} fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                <span>
                                                    Synced: {formatDate(lastSynced)}. {articleCount} article{articleCount !== 1 ? 's' : ''} indexed.
                                                </span>
                                            </div>
                                            <button
                                                style={styles.resyncButton}
                                                onClick={() => handleSync(source.id)}
                                                disabled={isSyncing}
                                                onMouseEnter={(e) => {
                                                    if (!isSyncing) {
                                                        e.target.style.backgroundColor = '#EBEEF0';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isSyncing) {
                                                        e.target.style.backgroundColor = '#F4F5F6';
                                                    }
                                                }}
                                            >
                                                {isSyncing ? 'Syncing...' : 'Re-sync Now'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Indexed Articles List */}
                                    {indexedArticles.length > 0 && (
                                        <div style={styles.articlesList}>
                                            {indexedArticles.map((article) => (
                                                <div key={article.id} style={styles.articleItem}>
                                                    <span style={styles.articleNumber}>{article.index}.</span>
                                                    <a 
                                                        href={article.url}
                                                        style={styles.articleTitle}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            navigate(article.url);
                                                        }}
                                                    >
                                                        {article.title}
                                                    </a>
                                                    <svg style={styles.articleLink} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                    </svg>
                                                    <span style={styles.articleDate}>{formatArticleDate(article.date)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Empty State */}
                    {simpleSources.length === 0 && complexSources.length === 0 && (
                        <div style={styles.emptyState}>
                            <div style={styles.emptyIcon}>
                                <svg style={{width: '100%', height: '100%'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 style={styles.emptyTitle}>
                                No sources connected
                            </h3>
                            <p style={styles.emptyText}>
                                Connect your first knowledge source to get started
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
